import React, { useState } from "react";
import { Archive, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type ImportType = "snapshot" | "received";

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (type: ImportType) => void;
}

interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const OptionCard: React.FC<OptionCardProps> = ({ selected, onClick, icon, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "w-full text-left rounded-lg border-2 p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:border-primary/40 hover:bg-muted/30"
    )}
  >
    <div className="flex items-start gap-3">
      <div className={cn("mt-0.5 shrink-0", selected ? "text-primary" : "text-muted-foreground")}>
        {icon}
      </div>
      <div>
        <p className="font-medium text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  </button>
);

const ImportTypeDialog: React.FC<Props> = ({ open, onClose, onContinue }) => {
  const [selected, setSelected] = useState<ImportType>("snapshot");

  const handleContinue = () => {
    onContinue(selected);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What would you like to import?</DialogTitle>
          <DialogDescription>
            Choose how to update inventory data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <OptionCard
            selected={selected === "received"}
            onClick={() => setSelected("received")}
            icon={<ArrowUpDown className="h-5 w-5" />}
            title="Inventory adjustment"
            description="Add or remove stock for one or more products."
          />
          <OptionCard
            selected={selected === "snapshot"}
            onClick={() => setSelected("snapshot")}
            icon={<Archive className="h-5 w-5" />}
            title="Inventory snapshot"
            description="Update historical stock levels used for reporting."
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleContinue}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportTypeDialog;
